// ConfigurationModal.js — side-by-side Source(JSONPath) ↔ Destination rows
// Fixes: single-click focus by avoiding state change on TextField focus,
// and removing over-memoization of Row.

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLockBodyScroll } from '../../../hooks/useLockBodyScroll';
import { useAuth } from '../../../contexts/AuthContext';
import { Modal, Card, BlockStack, Text, Button, TextField, Select, InlineStack, InlineGrid, Badge, Collapsible, Icon } from '@shopify/polaris';
import { getAuthHeaders } from '../../../services/api/authUtils';
import { configApi } from '../../../services/api/configApi';
import { SettingsIcon, DeleteIcon, PinFilledIcon, PinIcon, DataTableIcon, LanguageTranslateIcon, CheckIcon, SunIcon, TransferInIcon } from '@shopify/polaris-icons';
import jsonata from 'jsonata';
import JsonView from 'react18-json-view';
import 'react18-json-view/src/style.css';
import './ConfigurationModal.css';

const toRuleType = (m) => {
  const t = m?.transformation || m?.type || 'direct';
  if (t === 'direct' || t === 'static' || t === 'lookup') return t;
  if (t === 'javascript' || t === 'js' || t === 'transform') return 'formula';
  return 'direct';
};

// --- Helpers for unified left column (V4 behavior) ---
const iconForType = (t) => (t === 'formula' ? LanguageTranslateIcon : t === 'lookup' ? DataTableIcon : t === 'static' ? TransferInIcon : t === 'direct' ? SunIcon : null);
function isRowMapped(m) {
  if (!m) return false;
  const t = m.type || 'direct';
  if (t === 'direct') return !!(m.from && m.from.trim());
  if (t === 'static') return !!(String(m.value ?? '').trim());
  if (t === 'formula') return !!(m.js && m.js.trim());
  if (t === 'lookup') {
    if (m.lookup && m.lookup.tableData && Object.keys(m.lookup.tableData).length) return true;
    try {
      if (!m.lookupText) return false;
      const obj = JSON.parse(m.lookupText);
      return obj && typeof obj === 'object' && !Array.isArray(obj) && Object.keys(obj).length > 0;
    } catch {
      return false;
    }
  }
  return false;
}

const stripPayoutSettingsFields = (dto) => {
  if (!dto || typeof dto !== 'object') return dto;
  return {
    ...dto,
    fields: (dto.fields || []).filter((field) => {
      const path = field.destPath || field.destination || field.netsuitePath || '';
      return !String(path).startsWith('settings.payout.');
    }),
  };
};

// (sample path helper removed as unused)

export default function ConfigurationModal({ open, onClose, onSave, keyId = 'customers', entityLabel = 'Customers', config = {} }) {
  useLockBodyScroll(open);
  const { refresh } = useAuth();
  const keepAlive = useCallback(async () => { try { await refresh(); } catch (_) {} }, [refresh]);
  // In-memory sample JSON for the session
  const [parsedSource, setParsedSource] = useState({});
  const [sampleText, setSampleText] = useState(JSON.stringify({}, null, 2));
  const sampleRef = useRef(null);
  const transformRef = useRef(null);
  const initialSampleRef = useRef({});
  const initialSampleTextRef = useRef('{}');
  const snapshotRef = useRef({});
  const workingRef = useRef({});
  const didInitRef = useRef(false);
  const dirtyRef = useRef(false);
  const [errors, setErrors] = useState({});
  const [configError, setConfigError] = useState('');
  const [isFetchingSample, setIsFetchingSample] = useState(false);
  const [dtoData, setDtoData] = useState(null);
  const [nsSearches, setNsSearches] = useState([]);
  const [nsSearchId, setNsSearchId] = useState('');
  const [sampleVersion, setSampleVersion] = useState(0);
  const rootPathRef = useRef('');
  const [requiredSet, setRequiredSet] = useState(new Set());
  const [dtoSections, setDtoSections] = useState({});
  const [sectionOrder, setSectionOrder] = useState([]);
  // UI snapshot to decouple rendering from dtoData recomputations
  const [uiSections, setUiSections] = useState({});
  const [uiSectionOrder, setUiSectionOrder] = useState([]);
  const [addingCustom, setAddingCustom] = useState(false);
  const [newCustomPath, setNewCustomPath] = useState('');
  const [isTransforming, setIsTransforming] = useState(false);
  const [sampleEditMode, setSampleEditMode] = useState(false);
  const [sampleEditText, setSampleEditText] = useState('');

  // mappings: { id, destination, type, from (jsonpath), value, js, lookup }
  const [mappings, setMappings] = useState([]);
  const [activeKey, setActiveKey] = useState(null);

  // Use destination path as the stable key so newly added fields work immediately
  const makeKey = useCallback((dest, id, label) => `${dest}_${id}`, []);

  const initFromConfig = useCallback((cfg) => {
    // Load DTO and in-memory sample/saved search from DB
    const sample = cfg?.sourceSample || null;
    if (sample) {
      setParsedSource(sample);
      const txt = JSON.stringify(sample, null, 2);
      setSampleText(txt);
      initialSampleRef.current = sample;
      initialSampleTextRef.current = txt;
      requestAnimationFrame(() => {
        const el = sampleRef.current;
        if (el && el.textContent !== txt) {
          el.textContent = txt;
        }
      });
    } else {
      setParsedSource({});
      const txt = JSON.stringify({}, null, 2);
      setSampleText(txt);
      initialSampleRef.current = {};
      initialSampleTextRef.current = txt;
      setConfigError(`No configuration available. Please add DTO and sample source for ${entityLabel.toLowerCase()}.`);
      requestAnimationFrame(() => {
        const el = sampleRef.current;
        if (el && el.textContent !== txt) {
          el.textContent = txt;
        }
      });
    }
    const rawIncomingDto = cfg?.dto?.fields ? cfg.dto : (cfg?.dto?.dto || null);
    const incomingDto = keyId === 'payouts' ? stripPayoutSettingsFields(rawIncomingDto) : rawIncomingDto;
    const fields = Array.isArray(incomingDto?.fields) ? incomingDto.fields : [];
    setDtoData(incomingDto || null);
    rootPathRef.current = String(incomingDto?.settings?.rootPath || '').trim();
    const rawId =
      (incomingDto?.settings?.fetchSample?.query?.searchId)
      || (cfg?.settings?.fetchSample?.query?.searchId)
      || (incomingDto?.settings?.fetchSample?.savedSearchId)
      || (cfg?.settings?.fetchSample?.savedSearchId)
      || (incomingDto?.settings?.savedSearchId)
      || (cfg?.settings?.savedSearchId);
    const existingId = (typeof rawId === 'string' && rawId.includes('{{')) ? '' : rawId;
    setNsSearchId(existingId || '');
    if (!incomingDto) {
      setConfigError(`No configuration available. Please add DTO and sample source for ${entityLabel.toLowerCase()}.`);
    }
    const req = new Set();
    const sections = {};
    const order = [];
    const sorted = [...fields].sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999));
    sorted.forEach((f) => {
      if (f.required) req.add(f.destPath);
      const rawName = (f.section || 'additional');
      // Force any destPath under settings.* to render in the Settings section
      const sec = String(f?.destPath?.startsWith('settings.') ? 'settings' : rawName).toLowerCase();
      if (!sections[sec]) { sections[sec] = []; order.push(sec); }
      sections[sec].push({ destPath: f.destPath, label: f.label || f.destPath, section: sec, id: f.id });
    });
    if (!sections['custom']) { sections['custom'] = []; order.push('custom'); }
    setRequiredSet(req);
    setDtoSections(sections);
    // Ensure 'settings' section is first in order
    const reordered = order.includes('settings') ? ['settings', ...order.filter((s) => s !== 'settings')] : order;
    setSectionOrder(reordered);
    setUiSections(sections);
    setUiSectionOrder(reordered);
    setMappings([]);
    const newMappings = [];
    sorted.forEach((f) => {
      const m = f.mapping;
      let internal = { type: 'direct', from: '', policy: 'update' };
      if (m) {
        if (m.type === 'direct') internal = { type: 'direct', from: m.sourcePath || '', policy: 'update' };
        else if (m.type === 'static') internal = { type: 'static', value: String(m.value ?? ''), policy: 'update' };
        else if (m.type === 'formula') internal = { type: 'formula', js: m.expression || '', policy: 'update' };
        else if (m.type === 'lookup') internal = { type: 'lookup', from: m.sourcePath || '', lookup: { tableData: (m.value || {}) }, policy: 'update' };
        // apply persisted policy if present (backward-compat for older keys)
        const persisted = m.policy;
        internal.policy = (persisted === 'create_only' || persisted === 'create') ? 'create' : 'update';
      }
      const destinationKey = makeKey(f.destPath, f.id, f.label);
      newMappings.push({ id: f.id || `${Date.now()}`, fieldId: f.id, destination: f.destPath, destinationKey, type: 'direct', from: '', ...internal });
    });
    setMappings(newMappings);
    setActiveKey(null);
  }, [entityLabel, keyId, makeKey]);

  useEffect(() => {
    if (!open) return;
    if (didInitRef.current) return;
    initFromConfig(config);
    didInitRef.current = true;
  }, [open, keyId, initFromConfig]);

  useEffect(() => {
    if (!open) {
      didInitRef.current = false;
      dirtyRef.current = false;
    }
  }, [open]);

  // Load NetSuite saved searches only when source is NetSuite (Items/Images/Fulfillments/PriceLevels/Products)
  const nsKeys = ['images', 'fulfillment', 'fulfillments', 'prices', 'pricelevels', 'products', 'refunds'];
  const sourceProvider = (dtoData?.settings?.request?.provider || dtoData?.source || '').toLowerCase();
  const shouldLoadNsSearches = open && nsKeys.includes(keyId) && /netsuite/i.test(sourceProvider);
  useEffect(() => {
    if (!shouldLoadNsSearches) return;
    (async () => {
      try {
        const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:5001');
        const type = (keyId === 'fulfillments' || keyId === 'fulfillment') ? 'fulfillment' : 
                     (keyId === 'refunds') ? 'creditmemo' : 'item';
        const resp = await fetch(`${apiBase}/api/netsuite/saved-searches/${type}`, { headers: getAuthHeaders() });
        const json = await resp.json();
        if (json?.success && Array.isArray(json.searches)) setNsSearches(json.searches);
      } catch (_) {}
    })();
  }, [open, keyId, shouldLoadNsSearches]);

  // removed unused source hints

  // mapping helpers
  const getByKey = useCallback((key) => mappings.find((m) => m.destinationKey === key), [mappings]);

  const upsert = useCallback(
    (dest, id, label, patch) => {
      dirtyRef.current = true;
      return setMappings((prev) => {
        const destinationKey = makeKey(dest, id, label);
        const i = prev.findIndex((m) => m.destinationKey === destinationKey);
        if (i >= 0) {
          const c = [...prev];
          c[i] = { ...c[i], fieldId: id ?? c[i].fieldId, ...patch };
          return c;
        }
        return [
          ...prev,
          {
            id: id || `${Date.now()}`,
            fieldId: id,
            destination: dest,
            destinationKey,
            type: 'direct',
            from: '',
            ...patch,
          },
        ];
      });
    },
    [makeKey],
  );

  const removeMapping = useCallback((key) => {
    dirtyRef.current = true;
    setMappings((prev) => prev.filter((m) => m.destinationKey !== key));
  }, []);

  // Fetch latest sample from backend
  const handleFetchSample = async () => {
    try {
      // For non-items entities, fetch latest persisted sample via config; for items we use RESTlet button above
      const resp = await configApi.getConfigByKey(keyId);
      if (resp && resp.success && resp.configured && resp.data && resp.data.defaultSource) {
        const s = resp.data.defaultSource;
        setParsedSource(s);
        const txt = JSON.stringify(s, null, 2);
        setSampleText(txt);
        setConfigError('');
        requestAnimationFrame(() => { const el = sampleRef.current; if (el && el.textContent !== txt) el.textContent = txt; });
      } else {
        setConfigError('No sample available. Please save a configuration with defaultSource first.');
      }
    } catch (e) {
      setConfigError('Failed to fetch sample');
    }
  };

  // Simple JSONPath getter supporting dot and [index]; treats [*] as [0]
  const getByPath = (obj, path) => {
    if (!path) return undefined;
    const stripped = path.replace(/^\$json\./, '').replace(/^\$\./, '');
    const normalized = stripped.replace(/\[\*\]/g, '[0]');
    const parts = normalized
      .replace(/\[(\d+)\]/g, '.$1')
      .split('.')
      .filter(Boolean);
    let cur = obj;
    for (const p of parts) {
      if (cur == null) return undefined;
      cur = cur[p];
    }
    return cur;
  };

  // Setter to build nested objects/arrays by path
  const setByPath = (obj, path, value) => {
    if (!path) return;
    const normalized = path.replace(/\[\*\]/g, '[0]');
    const parts = normalized
      .replace(/\[(\d+)\]/g, '.$1')
      .split('.')
      .filter(Boolean);
    let cur = obj;
    parts.forEach((p, idx) => {
      const isLast = idx === parts.length - 1;
      const nextKey = parts[idx + 1];
      if (isLast) {
        cur[p] = value;
      } else {
        const shouldBeArray = /^(\d+)$/.test(nextKey);
        if (!(p in cur)) cur[p] = shouldBeArray ? [] : {};
        cur = cur[p];
      }
    });
  };

  // Helpers for transform preview to mirror backend behavior
  // IMPORTANT: These helpers are duplicated from backend-cartnxs/src/controllers/transformController.js
  // If you change these, update the backend version too!
  const pathHasWildcard = (pathString) => /\[\*\]/.test(String(pathString || ''));
  const registerJsonataHelpers = (expr) => {
    // $coalesce(a,b,c,...)
    try {
      expr.registerFunction('coalesce', function() {
        for (let i = 0; i < arguments.length; i += 1) {
          const v = arguments[i];
          if (v !== undefined && v !== null && !(Array.isArray(v) && v.length === 0)) return v;
        }
        return null;
      });
    } catch (_) { /* no-op */ }
    
    // $parseJson(jsonString): Parse JSON string to object
    try {
      expr.registerFunction('parseJson', (jsonString) => {
        if (!jsonString || typeof jsonString !== 'string') return null;
        try {
          return JSON.parse(jsonString);
        } catch (e) {
          return null;
        }
      });
    } catch (_) { /* no-op */ }
    
    // $weekNumber(isoDateString): Get ISO week number from date
    try {
      expr.registerFunction('weekNumber', (isoDateString) => {
        if (!isoDateString) return 1;
        const d = new Date(isoDateString);
        d.setHours(0, 0, 0, 0);
        d.setDate(d.getDate() + 4 - (d.getDay() || 7));
        const yearStart = new Date(d.getFullYear(), 0, 1);
        return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
      });
    } catch (_) { /* no-op */ }
    
    // $pad(str, length, char): Zero-pad a string to given length
    try {
      expr.registerFunction('pad', (str, length, char) => {
        const s = String(str || '');
        const len = Math.abs(Number(length) || 0);
        const c = String(char || '0');
        return s.padStart(len, c);
      });
    } catch (_) { /* no-op */ }
    
    // $splitLineItem(item, dtoSettings): Split Shopify line item into material + art lines
    try {
      expr.registerFunction('splitLineItem', (item, dtoSettings) => {
        if (!item || !item.variant || !item.variant.metafields) return [];
        
        const metafields = item.variant.metafields.edges || [];
        
        // Extract metafield values
        const getMeta = (key) => {
          const edge = metafields.find(e => e.node && e.node.key === key);
          return edge ? edge.node.value : null;
        };
        
        const materialId = getMeta('sage_material_id');
        const materialPriceStr = getMeta('sage_material_price');
        const artId = getMeta('sage_art_id');
        const artPriceStr = getMeta('sage_art_price');
        
        // Parse price JSON strings
        let materialPrice = 0;
        let artPrice = 0;
        
        try {
          if (materialPriceStr) {
            const parsed = JSON.parse(materialPriceStr);
            materialPrice = parseFloat(parsed.amount || 0);
          }
          if (artPriceStr) {
            const parsed = JSON.parse(artPriceStr);
            artPrice = parseFloat(parsed.amount || 0);
          }
        } catch (e) {
          return [];
        }
        
        // Validate all metafields present (allow 0 as valid price - !0 is truthy and incorrectly rejects)
        const validPrice = (p) => p != null && !Number.isNaN(p) && typeof p === 'number';
        if (!materialId || !validPrice(materialPrice) || !artId || !validPrice(artPrice)) {
          return [];
        }
        
        // Calculate discount split: use totalDiscountSet when present, else derive from discountAllocations
        const quantity = parseInt(item.quantity) || 1;
        let discountAmount = parseFloat(item.totalDiscountSet?.shopMoney?.amount || 0);
        if (discountAmount === 0 && Array.isArray(item.discountAllocations) && item.discountAllocations.length > 0) {
          discountAmount = item.discountAllocations.reduce((sum, a) => {
            return sum + parseFloat(a?.allocatedAmountSet?.shopMoney?.amount || 0);
          }, 0);
        }
        
        const materialTotal = materialPrice * quantity;
        const artTotal = artPrice * quantity;
        const combinedTotal = materialTotal + artTotal;
        
        // Guard: when both prices are 0, avoid division by zero (0/0 = NaN)
        const materialDiscountRatio = combinedTotal > 0 ? materialTotal / combinedTotal : 0;
        const artDiscountRatio = combinedTotal > 0 ? artTotal / combinedTotal : 0;
        
        const materialDiscount = discountAmount * materialDiscountRatio;
        const artDiscount = discountAmount * artDiscountRatio;
        
        const materialDiscountPercent = materialDiscount > 0 ? (materialDiscount / materialTotal) * 100 : 0;
        const artDiscountPercent = artDiscount > 0 ? (artDiscount / artTotal) * 100 : 0;
        
        // Get settings from dtoSettings parameter
        const defaultLocation = dtoSettings?.defaultLocation || '1';
        const defaultPriceList = dtoSettings?.defaultPriceList || 'CAN';
        
        return [
          {
            LineType: 'Item',
            Item: materialId,
            QuantityOrdered: quantity,
            QuantityShipped: quantity,
            OrderUnitOfMeasure: 'ITEM',
            OrderUnitPrice: materialPrice,
            PricingUnitPrice: materialPrice,  // Required for SAGE OE to honor price override
            PriceOverride: true,
            OrderDiscountAmount: materialDiscount,
            DiscountPercent: materialDiscountPercent,
            Description: (item.name || '') + ' - Material',
            Location: defaultLocation,
            PriceList: defaultPriceList
          },
          {
            LineType: 'Item',
            Item: artId,
            QuantityOrdered: quantity,
            QuantityShipped: quantity,
            OrderUnitOfMeasure: 'ITEM',
            OrderUnitPrice: artPrice,
            PricingUnitPrice: artPrice,  // Required for SAGE OE to honor price override
            PriceOverride: true,
            OrderDiscountAmount: artDiscount,
            DiscountPercent: artDiscountPercent,
            Description: (item.name || '') + ' - Art',
            Location: defaultLocation,
            PriceList: defaultPriceList
          }
        ];
      });
    } catch (_) { /* no-op */ }
  };

  const applyTransform = async (sourceObj, mapArray = mappings) => {
    const result = {};
    const appendCounters = {};
    for (const m of (mapArray || [])) {
      const dest = m.netsuitePath || m.destination;
      if (!dest) return;
      const type = m.type || 'direct';

      let value;
      if (type === 'static') {
        value = m.value;
      } else if (type === 'formula' && m.js) {
        try {
          const expr = jsonata(m.js);
          registerJsonataHelpers(expr);
          // Evaluate with helpers and bindings to mirror backend
          // For samples without grouping, treat single item as a group of one
          value = await expr.evaluate(sourceObj, { 
            json: sourceObj, 
            source: sourceObj, 
            dto: dtoData,
            groupedItems: sourceObj._groupedItems || [sourceObj]  // Make grouped items available
          });
        } catch (_) {
          value = undefined;
        }
      } else if (type === 'lookup' && m.lookup && m.lookup.tableData) {
        const key = String(getByPath(sourceObj, m.from) ?? '');
        value = m.lookup.tableData[key];
      } else {
        value = getByPath(sourceObj, m.from);
      }

      if (value === undefined) value = null;

      if (/\[\*\]/.test(dest)) {
        let arrValues = Array.isArray(value) ? value : [value];
        arrValues = arrValues.filter((v) => v != null);
        const base = String(dest).split('[*]')[0].replace(/\.$/, '');
        if (appendCounters[base] === undefined) {
          // initialize with current length if any
          const existing = getByPath(result, base);
          appendCounters[base] = Array.isArray(existing) ? existing.length : 0;
        }
        for (let i = 0; i < arrValues.length; i += 1) {
          const idx = appendCounters[base]++;
          const indexedPath = dest.replace('[*]', `[${idx}]`);
          setByPath(result, indexedPath, arrValues[i]);
        }
      } else {
        setByPath(result, dest, value);
      }
    }
    return result;
  };

  const handleRunTransform = async () => {
    setIsTransforming(true);
    try {
      await keepAlive();
      // Use the current sample (may have been edited by the user)
      // Use the full source envelope (parsedSource) — not displaySample which is already
      // unwrapped by rootPath for the preview panel. The backend transform applies rootPath
      // itself via selectFromRoot, so it needs the complete provider response.
      const obj = (() => {
        try {
          if (sampleEditMode) return JSON.parse(sampleEditText || '{}');
          return parsedSource && typeof parsedSource === 'object' ? parsedSource : JSON.parse(sampleText || '{}');
        } catch (_) { return {}; }
      })();
      // Build the current DTO including any unsaved field edits from workingRef
      const currentDto = buildCurrentDto();
      const resp = await configApi.transformByKey(keyId, { source: [obj], dto: currentDto, preview: true });
      const output = resp?.data?.[0] ?? resp;
      const outTxt = JSON.stringify(output, null, 2);
      requestAnimationFrame(() => {
        if (transformRef.current) transformRef.current.textContent = outTxt;
      });
    } catch (e) {
      const err = `Transform failed: ${e.message}`;
      requestAnimationFrame(() => {
        if (transformRef.current) transformRef.current.textContent = err;
      });
    } finally {
      setIsTransforming(false);
    }
  };

  // Build a DTO object from current dtoData + unsaved working edits (mirrors what would be saved)
  const buildCurrentDto = () => {
    if (!dtoData) return null;
    const fields = (dtoData.fields || []).map((f) => {
      const k = makeKey(f.destPath, f.id, f.label);
      const w = workingRef.current?.[k];
      if (!w) return f;
      const updated = { ...f, mapping: { ...f.mapping } };
      if (w.type) updated.mapping.type = w.type;
      if (w.type === 'direct' && w.from !== undefined) updated.mapping.sourcePath = w.from;
      if (w.type === 'static' && w.value !== undefined) updated.mapping.value = w.value;
      if (w.type === 'formula' && w.js !== undefined) { updated.mapping.expression = w.js; updated.mapping.engine = 'jsonata'; }
      if (w.type === 'lookup' && w.lookupText) {
        try { updated.mapping.value = JSON.parse(w.lookupText); } catch {}
        if (w.from) updated.mapping.sourcePath = w.from;
      }
      return updated;
    });
    return { ...dtoData, fields };
  };

  // JSON tree helpers
  const toJsonPath = (segments, useWildcard = true) => {
    const body = (segments || [])
      .map((seg) => (typeof seg === 'number' ? (useWildcard ? '[*]' : `[${seg}]`) : seg))
      .join('.');
    return `$json.${body}`;
  };
  const handlePathSelected = (segments) => {
    const path = toJsonPath(segments, true);
    try { navigator.clipboard?.writeText(path); } catch {}
    if (activeKey) {
      // map to active row as direct
      const base = workingRef.current[activeKey] ? { ...workingRef.current[activeKey] } : { type: 'direct' };
      base.type = 'direct';
      base.from = path;
      workingRef.current[activeKey] = base;
    }
  };

  // Keep ref DOM content in sync with state only when it actually changed externally
  useEffect(() => {
    const el = sampleRef.current;
    if (el && el.textContent !== sampleText) {
      // do not force set while user is typing; only external updates change sampleText
    }
  }, [sampleText]);

  // Compute a display sample that excludes the leading structure up to rootPath (e.g., items[*] -> items[0])
  const displaySample = (() => {
    try {
      // Prefer live dto.settings.rootPath; fallback to a Static mapping row for settings.rootPath
      let rp = String(dtoData?.settings?.rootPath || '').trim();
      if (!rp) {
        const m = (mappings || []).find((x) => x.destination === 'settings.rootPath' && (x.type === 'static' && typeof x.value === 'string'));
        if (m && m.value) rp = String(m.value).trim();
      }
      if (!rp) rp = (rootPathRef.current || '').trim();
      if (!rp) return parsedSource;
      const noWildcard = rp.replace(/\[\*\]/g, '[0]');
      const parts = noWildcard.replace(/\[(\d+)\]/g, '.$1').split('.').filter(Boolean);
      let cur = parsedSource;
      for (const p of parts) {
        if (cur == null) return parsedSource;
        cur = cur[p];
      }
      return (cur === undefined ? parsedSource : cur);
    } catch (_) {
      return parsedSource;
    }
  })();

  const handleSave = async () => {
    if (!dtoData) return onClose?.();
    await keepAlive();
    // Merge editor mappings back into dto.fields
    const byKeyMap = mappings.reduce((a, m) => { a[m.destinationKey] = m; return a; }, {});
    // Overlay unsaved row edits (workingRef) so first-time saves persist new mappings
    Object.entries(workingRef.current || {}).forEach(([key, w]) => {
      const base = byKeyMap[key] || { destinationKey: key };
      const t = (w?.type || 'direct');
      if (t === 'direct') byKeyMap[key] = { ...base, type: 'direct', from: w.from || '' };
      else if (t === 'static') byKeyMap[key] = { ...base, type: 'static', value: w.value };
      else if (t === 'formula') byKeyMap[key] = { ...base, type: 'formula', js: w.js || '' };
      else if (t === 'lookup') {
        let tableData = {};
        try { tableData = JSON.parse(w.lookupText || '{}'); } catch (_) {}
        byKeyMap[key] = { ...base, type: 'lookup', from: w.from || '', lookup: { tableData } };
      }
    });
    const updatedFields = dtoData.fields.map((f) => {
      const key = makeKey(f.destPath, f.id, f.label);
      const m = byKeyMap[key];
      if (!m) {
        const { mapping, ...rest } = f; // remove mapping if user cleared it
        return rest;
      }
      let mapping;
      if ((m.type || 'direct') === 'direct') mapping = { type: 'direct', sourcePath: m.from || '' };
      else if (m.type === 'static') mapping = { type: 'static', value: m.value };
      else if (m.type === 'formula') mapping = { type: 'formula', engine: 'jsonata', expression: m.js || '' };
      else if (m.type === 'lookup') mapping = { type: 'lookup', sourcePath: m.from || '', value: m.lookup?.tableData || {} };
      // persist sync policy on each field mapping (map old values to new)
      if (m.policy === 'create_only') mapping.policy = 'create';
      else if (m.policy === 'create') mapping.policy = 'create';
      else mapping.policy = 'update';
      return { ...f, mapping };
    });
    const dtoOut = { ...dtoData, fields: updatedFields };
    // Derive settings.* values and dto.mappings as before ...
    const setNested = (obj, path, val) => {
      const parts = String(path).split('.').filter(Boolean);
      let cur = obj;
      parts.forEach((p, idx) => {
        const last = idx === parts.length - 1;
        if (last) cur[p] = val; else { if (!(p in cur)) cur[p] = {}; cur = cur[p]; }
      });
    };
    const settingsFromDto = { ...(dtoOut.settings || dtoData?.settings || {}) };
    const mappingsFromDto = { ...(dtoOut.mappings || dtoData?.mappings || {}) };
    (updatedFields || []).forEach((f) => {
      const dp = f.destPath || '';
      if (!dp.startsWith('settings.')) return;
      const tail = dp.slice('settings.'.length);
      const m = f?.mapping || {};
      if (m.type === 'lookup') {
        const mapVal = m.value || {};
        if (mapVal && typeof mapVal === 'object' && !Array.isArray(mapVal)) {
          setNested(settingsFromDto, tail, mapVal);
        }
      } else if (m.type === 'static') {
        setNested(settingsFromDto, tail, m.value);
      }
      // For formula/direct we do not persist into settings automatically
    });
    // Also derive dto.mappings.* from fields whose destPath starts with 'mappings.'
    (updatedFields || []).forEach((f) => {
      const dp = f.destPath || '';
      if (!dp.startsWith('mappings.')) return;
      const tail = dp.slice('mappings.'.length);
      const mapVal = f?.mapping?.type === 'lookup' ? (f.mapping.value || {}) : undefined;
      if (mapVal && typeof mapVal === 'object' && !Array.isArray(mapVal)) {
        setNested(mappingsFromDto, tail, mapVal);
      }
    });
    // Persist selected saved search and current in-memory sample so it's preselected next time
    if (keyId === 'images' || keyId === 'fulfillments' || keyId === 'fulfillment' || keyId === 'prices' || keyId === 'pricelevels' || keyId === 'products' || keyId === 'refunds') {
      const fs = { ...(settingsFromDto.fetchSample || {}) };
      const q = { ...(fs.query || {}) };
      // Persist the selected saved search under query.searchId only
      q.searchId = nsSearchId || '';
      // Also persist under settings.savedSearchId for placeholder-based DTOs
      settingsFromDto.savedSearchId = nsSearchId || '';
      settingsFromDto.fetchSample = { ...fs, query: q };
    }
    const dtoWithSettings = { ...dtoOut, settings: settingsFromDto, mappings: mappingsFromDto };
    // Persist only inside dto; avoid duplicating settings at top-level data.settings
    onSave?.(keyId, { dto: dtoWithSettings, defaultSource: parsedSource, lastUpdated: new Date().toISOString() });
    onClose?.();
  };

  // editorRule removed
  const transformationOptions = [
    { label: 'Direct Mapping - Use value as-is', value: 'direct' },
    { label: 'Formula - Apply mathematical or text transformation', value: 'formula' },
    { label: 'Lookup Table - Map to predefined values', value: 'lookup' },
    { label: 'Static Value - Use hardcoded value', value: 'static' },
  ];

  const isRequired = (name) => requiredSet.has(name);

  // ---- Simple Row Component ----
  function Row({ destPath, label, isCustom = false, onDeleteField, id }) {
    const rowKey = makeKey(destPath, id, label);
    const base = getByKey(rowKey);
    // Overlay unsaved editor changes (if any) for display without requiring Save
    const overlay = (() => {
      const w = workingRef.current[rowKey];
      if (!w) return null;
      const t = (w.type || 'direct');
      if (t === 'direct') return { type: 'direct', from: w.from || '' };
      if (t === 'static') return { type: 'static', value: w.value };
      if (t === 'formula') return { type: 'formula', js: w.js || '' };
      if (t === 'lookup') {
        let tableData = {};
        try { tableData = JSON.parse(w.lookupText || '{}'); } catch { tableData = {}; }
        return { type: 'lookup', from: w.from || '', lookup: { tableData } };
      }
      return null;
    })();
    const m = overlay ? { ...(base || {}), ...overlay } : (base || {});
    const typeIcon = iconForType(m?.type || 'direct');
    const mapped = isRowMapped(m);
    const summary = (() => {
      if (!m) return 'Not set';
      const t = m.type || 'direct';
      if (t === 'direct') return m.from ? `${m.from}` : 'Not set';
      if (t === 'static') return m.value !== undefined ? `"${m.value}"` : 'Not set';
      if (t === 'formula') return m.js ? (m.js.length > 40 ? `${m.js.slice(0,40)}…` : m.js) : 'Formula not set';
      if (t === 'lookup') {
        const c = m.lookup && m.lookup.tableData ? Object.keys(m.lookup.tableData).length : 0;
        return c ? `${c} entries` : 'Lookup empty';
      }
      return 'Not set';
    })();

    const [local, setLocal] = useState(m || {});
    const [hover, setHover] = useState(false);
    useEffect(() => {
      if (activeKey === rowKey) {
        const cur = getByKey(rowKey) || { type: 'direct' };
        // prepare local copy including lookupText for editor use
        const lt = cur.lookup && cur.lookup.tableData ? JSON.stringify(cur.lookup.tableData, null, 2) : (cur.lookupText || '');
        const normalizedType = cur.type === 'javascript' ? 'formula' : (cur.type || 'direct');
        setLocal({ type: normalizedType, from: cur.from || '', value: cur.value, js: cur.js, lookupText: lt });
        snapshotRef.current[rowKey] = { ...cur };
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeKey, destPath, id, label]);

    return (
      <div style={{ padding: '16px', borderBottom: '1px solid #e1e3e5', transition: 'all 0.15s ease', marginBottom: 5 }} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
        <div style={{ display: 'grid', gridTemplateColumns: '90% 10%', alignItems: 'center', columnGap: 8 }}>
          <div style={{ minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
            <InlineStack gap="tight" align="center" style={{ paddingLeft: 8, flexWrap: 'wrap' }}>
              {mapped ? (
                <div style={{ marginLeft: 12, display: 'inline-flex' }}>
                  <Icon source={CheckIcon} />
                </div>
              ) : null}
              <Text variant="headingSm" fontWeight="bold">{label}</Text>
              {isRequired(destPath) ? (
                <div style={{ marginLeft: 8 }}>
                  <Badge tone="critical">Required</Badge>
                </div>
              ) : null}
              {typeIcon ? (
                <div style={{ marginLeft: 12, display: 'inline-flex' }}>
                  <Icon source={typeIcon} />
                </div>
              ) : null}
              <Text tone="subdued" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word', whiteSpace: 'normal', flex: 1, minWidth: 0 }}>{summary}</Text>
            </InlineStack>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', opacity: hover ? 1 : 0.45, transition: 'opacity 120ms ease' }}>
            {/* Gear first */}
            <Button
              variant="plain"
              size="slim"
              icon={SettingsIcon}
              onClick={() => {
                setActiveKey((prev) => (prev === rowKey ? null : rowKey));
                const cur = getByKey(rowKey) || { type: 'direct', from: '', value: '', js: '' };
                const lt = cur.lookup && cur.lookup.tableData ? JSON.stringify(cur.lookup.tableData, null, 2) : (cur.lookupText || '');
                const normalizedType = cur.type === 'javascript' ? 'formula' : (cur.type || 'direct');
                const policy = (cur.policy === 'create' || cur.policy === 'create_only') ? 'create' : 'update';
                workingRef.current[rowKey] = { type: normalizedType, from: cur.from || '', value: cur.value, js: cur.js, lookupText: lt, policy };
                setLocal({ type: normalizedType, from: cur.from || '', value: cur.value, js: cur.js, lookupText: lt, policy });
              }}
              accessibilityLabel="Configure mapping"
            />
            {/* Then pin toggle */}
            <Button
              variant="plain"
              size="slim"
              icon={((getByKey(rowKey)?.policy) === 'update') ? PinFilledIcon : PinIcon}
              onClick={() => {
                dirtyRef.current = true;
                const current = getByKey(rowKey) || { policy: 'update' };
                const currentPolicy = current.policy || 'update';
                const nextPolicy = currentPolicy === 'update' ? 'create' : 'update';
                // Only update the persisted mapping; do not touch workingRef so mappings are not cleared
                setMappings((prev) => prev.map(m => m.destinationKey === rowKey ? { ...m, policy: nextPolicy } : m));
              }}
              accessibilityLabel="Toggle sync policy (Create / Update)"
            />
            {isCustom && (
              <Button
                variant="plain"
                size="slim"
                tone="critical"
                icon={DeleteIcon}
                onClick={() => {
                  if (onDeleteField) onDeleteField(rowKey, destPath);
                  else removeMapping(rowKey);
                }}
                accessibilityLabel="Remove mapping"
              />
            )}
          </div>
        </div>

        {/* Inline editor */}
        <Collapsible open={activeKey === rowKey}>
          <Card sectioned style={{ padding: '16px' }}>
            <BlockStack gap="tight">
              {/* local/effective merge removed */}
              <div>
                <Select
                  label="Rule type"
                  options={transformationOptions}
                  value={(workingRef.current[rowKey]?.type) || (local.type || 'direct')}
                  onChange={(val) => {
                    const base = workingRef.current[rowKey] ? { ...workingRef.current[rowKey] } : { ...local };
                    base.type = val;
                    workingRef.current[rowKey] = base;
                    setLocal(base);
                    setErrors((prev) => ({ ...prev, [rowKey]: {} }));
                  }}
                />
              </div>

              {((workingRef.current[rowKey]?.type) || local.type) === 'direct' && (
                <div>
                  <BlockStack gap="tight">
                    <TextField
                      label="Source JSONPath"
                      value={(workingRef.current[rowKey]?.from) ?? (local.from || '')}
                      onChange={(v) => {
                        const base = workingRef.current[rowKey] ? { ...workingRef.current[rowKey] } : { ...local };
                        base.from = v;
                        workingRef.current[rowKey] = base;
                        setLocal(base);
                      }}
                      error={errors[rowKey]?.from}
                    />
                  </BlockStack>
                </div>
              )}

              {((workingRef.current[rowKey]?.type) || local.type) === 'static' && (
                <div>
                  <BlockStack gap="tight">
                    <TextField
                      label="Static value"
                      value={(workingRef.current[rowKey]?.value) ?? (local.value ?? '')}
                      onChange={(v) => {
                        const base = workingRef.current[rowKey] ? { ...workingRef.current[rowKey] } : { ...local };
                        base.value = v;
                        workingRef.current[rowKey] = base;
                        setLocal(base);
                      }}
                      placeholder="Enter the hardcoded value to use for this field"
                      helpText="This value will be used regardless of the source field value"
                      error={errors[rowKey]?.value}
                    />
                  </BlockStack>
                </div>
              )}

              {((workingRef.current[rowKey]?.type) || local.type) === 'formula' && (
                <div>
                  <BlockStack gap="tight">
                    <TextField
                      label="Formula mapping"
                      value={(workingRef.current[rowKey]?.js) ?? (local.js ?? '')}
                      onChange={(v) => {
                        const base = workingRef.current[rowKey] ? { ...workingRef.current[rowKey] } : { ...local };
                        base.js = v;
                        workingRef.current[rowKey] = base;
                        setLocal(base);
                      }}
                      placeholder="Enter formula"
                      multiline={2}
                      error={errors[rowKey]?.js}
                    />
                  </BlockStack>
                </div>
              )}

              {((workingRef.current[rowKey]?.type) || local.type) === 'lookup' && (
                <div>
                  <BlockStack gap="tight">
                    <TextField
                      label="Source JSONPath"
                      value={(workingRef.current[rowKey]?.from) ?? (local.from || '')}
                      onChange={(v) => {
                        const base = workingRef.current[rowKey] ? { ...workingRef.current[rowKey] } : { ...local };
                        base.from = v;
                        workingRef.current[rowKey] = base;
                        setLocal(base);
                      }}
                      placeholder="$json.status"
                      error={errors[rowKey]?.from}
                    />
                    <TextField
                      label="Lookup mapping"
                      value={(workingRef.current[rowKey]?.lookupText) ?? (local.lookupText || '')}
                      onChange={(v) => {
                        const base = workingRef.current[rowKey] ? { ...workingRef.current[rowKey] } : { ...local };
                        base.lookupText = v;
                        workingRef.current[rowKey] = base;
                        setLocal(base);
                      }}
                      placeholder="{'1':'Simple','2':'Configurable'}"
                      multiline={3}
                      error={errors[rowKey]?.lookupText}
                    />
                  </BlockStack>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', marginTop: '15px' }}>
                <Button
                  tone="critical"
                  onClick={() => {
                    dirtyRef.current = true;
                    removeMapping(rowKey);
                    // Clear any unsaved editor state for this row so summary updates immediately
                    delete workingRef.current[rowKey];
                    delete snapshotRef.current[rowKey];
                    setLocal({});
                    setActiveKey(null);
                  }}
                  icon={DeleteIcon}
                >
                  Remove Mapping
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    // Validate like V4 and show inline errors
                    const candidate = workingRef.current[rowKey] ? { ...workingRef.current[rowKey] } : local;
                    const err = {};
                    const t = candidate.type || 'direct';
                    if (t === 'direct') {
                      const from = (candidate.from || '').trim();
                      if (!from) {
                        err.from = 'Enter a source path';
                      } else if (!from.startsWith('$json.')) {
                        err.from = 'Path must start with $json.';
                      }
                    } else if (t === 'static') {
                      if (!(String(candidate.value || '').trim())) err.value = 'Enter a value';
                    } else if (t === 'formula') {
                      const exprText = (candidate.js || '').trim();
                      if (!exprText) {
                        err.js = 'Enter a formula';
                      } else {
                        try { jsonata(exprText); } catch (e) { err.js = 'Invalid JSONata'; }
                      }
                    } else if (t === 'lookup') {
                      const fromPath = (candidate.from || '').trim();
                      if (!fromPath) {
                        err.from = 'Enter a source path';
                      } else if (!fromPath.startsWith('$json.')) {
                        err.from = 'Path must start with $json.';
                      }
                      try {
                        const obj = JSON.parse(candidate.lookupText || '');
                        if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
                          err.lookupText = 'Provide a JSON object key→value';
                        } else if (Object.keys(obj).length === 0) {
                          err.lookupText = 'At least 1 entry required';
                        }
                      } catch (e) {
                        err.lookupText = 'Invalid JSON';
                      }
                    }
                    setErrors((prev) => ({ ...prev, [rowKey]: err }));
                    if (Object.keys(err).length) return;
                    let payload = { type: t, from: candidate.from, value: candidate.value, js: candidate.js };
                    if (t === 'lookup') {
                      const obj = JSON.parse(candidate.lookupText);
                      payload = { ...payload, lookup: { tableData: obj } };
                    }
                    upsert(destPath, id, label, payload);
                    setActiveKey(null);
                  }}
                >
                  Save
                </Button>
              </div>
            </BlockStack>
          </Card>
        </Collapsible>
      </div>
    );
  }
  // ---- Layout lists ----
  // Legacy fallback lists removed; DTO-only rendering

  // ---- Top-level modal ----
  return (
    <div className="config-modal-wrapper">
    <Modal
      open={open}
      onClose={onClose}
      title={`${entityLabel} Mapping Configuration`}
      size="large"
      primaryAction={{ content: 'Save Configuration', onAction: handleSave }}
      secondaryActions={[{ content: 'Cancel', onAction: onClose }]}
    >
      <Modal.Section>
        <InlineStack align="space-between" blockAlign="center" style={{ marginBottom: 12 }}>
          <div>
            <Text variant="headingMd" fontWeight="bold">Field Mapping Configuration</Text>
            <Text variant="bodyMd" tone="subdued">Map {dtoData?.source || 'Source'} data to {dtoData?.destination || 'Destination'} schema</Text>
            {configError ? (<Text tone="critical" style={{ marginTop: 8 }}>{configError}</Text>) : null}
          </div>
          <InlineStack align="end" blockAlign="center" gap="loose" style={{ paddingRight: 4 }}>
            {(() => {
              const nsKeys = ['images', 'fulfillment', 'fulfillments', 'prices', 'pricelevels', 'products', 'refunds'];
              const hasNsKey = nsKeys.includes(keyId);
              const sourceProvider = (dtoData?.settings?.request?.provider || dtoData?.source || '').toLowerCase();
              const isNetsuite = /netsuite/i.test(sourceProvider);
              const useNsSavedSearch = hasNsKey && isNetsuite;
              const resolveProvider = () => {
                const s = String(dtoData?.source || dtoData?.settings?.request?.provider || '');
                if (/adobe/i.test(s)) return 'adobe';
                if (/netsuite/i.test(s)) return 'netsuite';
                if (/shopify/i.test(s)) return 'shopify';
                return s ? s.toLowerCase() : undefined;
              };
              const handleFetchSample = async (useOverrides = false, overrides = {}) => {
                setIsFetchingSample(true);
                try {
                  setConfigError('');
                  const provider = resolveProvider();
                  const resp = useOverrides
                    ? await configApi.fetchSampleByKeyWithOverrides(keyId, provider, overrides)
                    : await configApi.fetchSampleByKey(keyId, provider);
                  if (resp && resp.success) {
                    const sample = resp.data ?? {};
                    const isEmpty = !sample || (typeof sample === 'object' && Object.keys(sample).length === 0);
                    const fallbackMsg = resp.fallbackError;
                    setParsedSource(sample);
                    const txt = JSON.stringify(sample, null, 2);
                    setSampleText(txt);
                    initialSampleRef.current = sample;
                    initialSampleTextRef.current = txt;
                    setSampleVersion((v) => v + 1);
                    requestAnimationFrame(() => { const el = sampleRef.current; if (el && el.textContent !== txt) el.textContent = txt; });
                    if (fallbackMsg) {
                      setConfigError(`Fetch failed: ${fallbackMsg}. Showing cached sample if available.`);
                    } else if (isEmpty && (keyId === 'fulfillments' || keyId === 'fulfillment')) {
                      setConfigError('No fulfilled orders found in Shopify. Ensure you have orders with fulfillment_status:fulfilled.');
                    }
                  } else {
                    setConfigError(resp?.error || 'Failed to fetch sample');
                  }
                } catch (e) {
                  setConfigError(e?.message || 'Failed to fetch sample');
                } finally { setIsFetchingSample(false); }
                }
              if (useNsSavedSearch) {
                return (
                  <InlineStack align="center" blockAlign="center" gap="base">
                    <div style={{ minWidth: 320 }}>
                      <Select
                        label="Saved Search"
                        labelHidden
                        options={[{ label: 'Select saved search …', value: '' }, ...nsSearches.map((s) => ({ label: s.name || String(s.id), value: String(s.id) }))]}
                        value={nsSearchId}
                        onChange={(v) => setNsSearchId(v)}
                      />
                    </div>
                    <div style={{ width: 12 }} />
                    <Button variant="secondary" size="medium" loading={isFetchingSample} onClick={async () => {
                      if (!nsSearchId) { setConfigError('Select a saved search first'); return; }
                      await handleFetchSample(true, { query: { searchId: nsSearchId || '' } });
                    }}>Fetch Sample</Button>
                  </InlineStack>
                );
              }
              return (
                <Button variant="secondary" size="medium" loading={isFetchingSample} onClick={() => handleFetchSample(false)}>Fetch Sample</Button>
              );
            })()}
            <div style={{ width: 6 }} />
            <Button variant="primary" size="medium" onClick={handleRunTransform} loading={isTransforming}>
              Run Transform
            </Button>
          </InlineStack>
        </InlineStack>

        <div style={{ display: 'flex', gap: '16px', height: '600px' }}>
          {/* Left side: Unified fields */}
          <div style={{ flex: '0 0 60%', overflowY: 'auto', paddingRight: '16px' }}>
            <Card>
              <BlockStack gap="loose">
                {dtoData ? (
                  <>
                    {uiSectionOrder.map((secName) => (
                      <div key={secName}>
                        <Text variant="headingSm" fontWeight="bold" as="h3" style={{ marginBottom: 8, padding: '0 20px' }}>
                          {secName === 'addresses' ? 'Addresses (Array)' : (secName.charAt(0).toUpperCase() + secName.slice(1))}
                        </Text>
                        {(uiSections[secName] || []).map((f) => (
                          <Row key={f.id || f.destPath} destPath={f.destPath} label={f.label} id={f.id} isCustom={secName.toLowerCase() === 'custom'}
                            onDeleteField={(rowKey, d) => {
                              if (secName !== 'custom') return;
                              dirtyRef.current = true;
                              setDtoData((prev) => ({ ...prev, fields: (prev.fields || []).filter(x => x.destPath !== d) }));
                              setDtoSections((prev) => ({ ...prev, [secName]: (prev[secName] || []).filter(x => x.destPath !== d) }));
                              setUiSections((prev) => ({ ...prev, [secName]: (prev[secName] || []).filter(x => x.destPath !== d) }));
                              setMappings((prev) => prev.filter(m => m.destinationKey !== rowKey));
                              delete workingRef.current[rowKey];
                              delete snapshotRef.current[rowKey];
                              setActiveKey(null);
                            }}
                          />
                        ))}
                      </div>
                    ))}

                    {/* Global Add New Field at the bottom in the 'custom' section */}
                    <div style={{ padding: '12px 20px' }}>
                      {!addingCustom ? (
                        <Button variant="secondary" onClick={() => { setAddingCustom(true); setNewCustomPath(''); }}>
                          + Add New Field
                        </Button>
                      ) : (
                        <div>
                          <TextField
                            label="Destination Field Path"
                            placeholder="custbody.myfield or items[*].customField"
                            value={newCustomPath}
                            onChange={(v) => setNewCustomPath(v)}
                            autoComplete="off"
                          />
                          <div style={{ height: 14 }} />
                          <InlineStack align="end" gap="none" style={{ paddingTop: 12, position: 'relative', zIndex: 2 }}>
                            <Button onClick={() => { setAddingCustom(false); setNewCustomPath(''); }}>Cancel</Button>
                            <div style={{ width: 14 }} />
                            <Button variant="primary" onClick={() => {
                              const dest = (newCustomPath || '').trim();
                              if (!dest) return;
                              if ((dtoData?.fields || []).some(f => f.destPath === dest)) { setAddingCustom(false); setNewCustomPath(''); return; }
                              const newId = `custom_${Date.now()}`;
                              setDtoData((prev) => {
                                const nextOrder = ((prev.fields || []).reduce((a, f) => Math.max(a, f.order || 0), 0)) + 10;
                                const fields = [...(prev.fields || []), { id: newId, destPath: dest, label: dest, section: 'custom', order: nextOrder, isCustom: true }];
                                return { ...prev, fields };
                              });
                              setDtoSections((prev) => {
                                const next = { ...prev };
                                if (!next.custom) next.custom = [];
                                next.custom = [...next.custom, { destPath: dest, label: dest, id: newId }];
                                if (!sectionOrder.includes('custom')) setSectionOrder([...sectionOrder, 'custom']);
                                return next;
                              });
                              setUiSections((prev) => {
                                const next = { ...prev };
                                if (!next.custom) next.custom = [];
                                next.custom = [...next.custom, { destPath: dest, label: dest, id: newId }];
                                if (!uiSectionOrder.includes('custom')) setUiSectionOrder([...uiSectionOrder, 'custom']);
                                return next;
                              });
                              upsert(dest, newId, dest, { type: 'direct', from: '' });
                              setAddingCustom(false);
                              setNewCustomPath('');
                            }}>Add Field</Button>
                          </InlineStack>
                        </div>
                      )}
                    </div>
                  </>
                ) : null}
              </BlockStack>
            </Card>
          </div>

          {/* Right side: Preview */}
          <div style={{ flex: '0 0 40%', width: '40%', display: 'flex', flexDirection: 'column', gap: '16px', paddingRight: '16px' }}>
            <div style={{
              border: 'none',
              borderRadius: '8px',
              padding: '16px',
              height: '280px',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <InlineStack align="space-between" blockAlign="center" style={{ marginBottom: '8px' }}>
                <Text variant="headingSm" fontWeight="bold" style={{ color: '#d82c0d' }}>
                  Sample Data
                </Text>
                <Button
                  variant="plain"
                  size="slim"
                  onClick={() => {
                    if (!sampleEditMode) {
                      setSampleEditText(JSON.stringify(displaySample, null, 2));
                    } else {
                      try {
                        const parsed = JSON.parse(sampleEditText);
                        setParsedSource(parsed);
                        setSampleText(JSON.stringify(parsed, null, 2));
                        setSampleVersion((v) => v + 1);
                      } catch (e) {
                        setConfigError('Invalid JSON in sample editor: ' + e.message);
                        return;
                      }
                    }
                    setSampleEditMode((m) => !m);
                  }}
                >
                  {sampleEditMode ? 'Apply' : 'Edit'}
                </Button>
              </InlineStack>
              <div style={{
                flex: 1,
                background: '#f8f9fa',
                border: '1px solid #e1e3e5',
                borderRadius: '4px',
                padding: 8,
                fontSize: '12px',
                overflow: 'auto'
              }}>
                {sampleEditMode ? (
                  <textarea
                    style={{ width: '100%', height: '100%', border: 'none', background: 'transparent', resize: 'none', fontFamily: 'monospace', fontSize: '12px', outline: 'none' }}
                    value={sampleEditText}
                    onChange={(e) => setSampleEditText(e.target.value)}
                    spellCheck={false}
                  />
                ) : (
                  <JsonView
                    src={displaySample}
                    collapsed={false}
                    enableClipboard={true}
                    displaySize={false}
                    displayArrayIndex={true}
                    customizeCopy={(node, meta) => {
                      const segments = (meta?.currentPath || []).filter((v) => v !== undefined);
                      const path = toJsonPath(segments, true);
                      handlePathSelected(segments);
                      return path;
                    }}
                  />
                )}
              </div>
            </div>

            <div style={{
              border: 'none',
              borderRadius: '8px',
              padding: '16px',
              height: '280px',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <Text variant="headingSm" fontWeight="bold" style={{ marginBottom: '12px', color: '#d82c0d' }}>
                Transformed Data
              </Text>
              <div style={{
                  flex: 1,
                  background: '#f8f9fa',
                  border: '1px solid #e1e3e5',
                  borderRadius: '4px',
                  padding: 0,
                  fontSize: '12px',
                  overflow: 'hidden',
                  display: 'flex',
                flexDirection: 'column'
              }}>
                <div
                  contentEditable={false}
                  ref={transformRef}
                  style={{
                    flex: 1,
                    padding: '12px',
                    whiteSpace: 'pre',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                    lineHeight: 1.4,
                    overflow: 'auto'
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </Modal.Section>
    </Modal>
    </div>
  );
}
